
import { SimulationContext } from "../simulation/simulation_context.js";

const BLOCK_MASS_MULTIPLIER = 1;

export const PhysicsStack = {
  additional_solver_iterations: 8,
} as const;

// Per-cell base masses. The live values in PhysicsMass are base × multiplier;
// the multiplier can be retuned per player segment from the server config
// (see LevelPackService) — entities read PhysicsMass at construction, so a new
// multiplier applies from the next level build. The cannon ball is exempt.
const BLOCK_MASS_BASE = {
  plywood_box: 10,
  brick_box: 22,
  stone_box: 40,
  column_per_cell: 30,
  colored_plywood_box: 12,
  plastic_box: 6,

  cone: 8,
  glass_jar_per_cell: 18,
  metal_block_per_cell: 8,
  ice_block_per_cell: 30,
  tree_block_per_cell: 18,
  watermelon_block: 20,
} as const;

export const PhysicsMass: { cannon_ball: number } & Record<keyof typeof BLOCK_MASS_BASE, number> = {
  cannon_ball: 7.44,
  ...BLOCK_MASS_BASE,
};

export function setBlockMassMultiplier( multiplier: number ): number
{
  // Simulation and stability checks always measure BASE masses: their numbers
  // feed the balance curve, so the verdict must not depend on whichever
  // segment multiplier the server handed this device. While the sim context
  // is active every request is pinned to ×1 — including a LevelPackService
  // refresh that lands mid-run.
  const requested = SimulationContext.enabled ? BLOCK_MASS_MULTIPLIER : multiplier;
  const safe = Number.isFinite( requested ) && requested > 0 ? requested : BLOCK_MASS_MULTIPLIER;
  for ( const key of Object.keys( BLOCK_MASS_BASE ) as ( keyof typeof BLOCK_MASS_BASE )[] )
    PhysicsMass[key] = BLOCK_MASS_BASE[key] * safe;
  return safe;
}

setBlockMassMultiplier( BLOCK_MASS_MULTIPLIER );

/**
 * Surface grip. Mass does not control sliding, so it is balanced separately.
 */
export const PhysicsFriction = {
  cannon_ball: 1,
  plywood: 0.7,
  brick: 0.8,
  stone: 0.75,
  glass: 0.45,
  ice: 0.25,
  cone: 0.6,
  metal_block: 0.7,
  plastic: 0.6,
  debris: 0.5,
} as const;

/**
 * Bounciness of gameplay objects.
 */
export const PhysicsRestitution = {
  cannon_ball: 0.15,
  solid_block: 0.05,
  glass: 0.08,
  ice: 0.08,
  cone: 0.05,
  metal_block: 0.05,
  debris: 0.1,
} as const;


export const PhysicsBreakSpeed = {
  glass_jar: 2.5,
  ice_block: 1.5,
} as const;
